import { useState } from "react";
import OnboardingBanner from "@/components/OnboardingBanner";
import {
  MagnifyingGlass, MapPin, Money, House, Key, Scroll,
  ArrowSquareOut, Copy, Check, Sparkle, CurrencyInr, Clock,
  CheckCircle, Warning, Couch, Car, Elevator, ShieldCheck,
  Lightning, PersonSimpleSwim, Barbell, Tree, Train, Airplane,
  Buildings, Hospital, ShoppingCart, TrendUp, ClipboardText,
  SealWarning, Handshake, Globe, Star,
  WifiHigh, Snowflake, Camera, PawPrint, LockSimple, Baby,
  PersonSimpleRun, Drop, BellRinging, Fire, Television, WashingMachine
} from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { INDIA_CITIES } from "@/lib/utils";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACQUISITION_TYPES = [
  { id: "purchase", label: "Buy", icon: House, desc: "Purchase outright" },
  { id: "rent", label: "Rent", icon: Key, desc: "Monthly rent + deposit" },
  { id: "lease", label: "Lease", icon: Scroll, desc: "Pagdi / Leave & License" },
];
const BEDROOM_OPTIONS = ["Studio", "1BHK", "2BHK", "3BHK", "4BHK", "Villa"];
const FURNISHING_OPTIONS = ["Any", "Unfurnished", "Semi-Furnished", "Fully Furnished"];
const AMENITIES = [
  { id: "Parking",          icon: Car },
  { id: "Lift",             icon: Elevator },
  { id: "Security",         icon: ShieldCheck },
  { id: "Power Backup",     icon: Lightning },
  { id: "Swimming Pool",    icon: PersonSimpleSwim },
  { id: "Gym",              icon: Barbell },
  { id: "Garden",           icon: Tree },
  { id: "WiFi",             icon: WifiHigh },
  { id: "AC",               icon: Snowflake },
  { id: "Furnished",        icon: Couch },
  { id: "CCTV",             icon: Camera },
  { id: "Pet Friendly",     icon: PawPrint },
  { id: "Gated Society",    icon: LockSimple },
  { id: "Club House",       icon: Buildings },
  { id: "Kids Play Area",   icon: Baby },
  { id: "Jogging Track",    icon: PersonSimpleRun },
  { id: "24x7 Water",       icon: Drop },
  { id: "Gas Pipeline",     icon: Fire },
  { id: "TV Cable",         icon: Television },
  { id: "Washing Machine",  icon: WashingMachine },
  { id: "Concierge",        icon: BellRinging },
];
const NEAR_OPTIONS = [
  { id: "Metro Station", icon: Train, label: "Metro" },
  { id: "Airport", icon: Airplane, label: "Airport" },
  { id: "IT Park", icon: Buildings, label: "IT Park" },
  { id: "Hospital", icon: Hospital, label: "Hospital" },
  { id: "Mall", icon: ShoppingCart, label: "Mall" },
];
const BUDGET_PRESETS = {
  purchase: ["₹25L", "₹50L", "₹75L", "₹1Cr", "₹1.5Cr", "₹2Cr"],
  rent:     ["₹10,000", "₹20,000", "₹35,000", "₹50,000", "₹75,000", "₹1,00,000"],
  lease:    ["₹5L", "₹10L", "₹20L", "₹35L", "₹50L", "₹1Cr"],
};

const PORTAL_STYLES = {
  nobroker:    { bg: "bg-emerald-50", border: "border-emerald-200", btn: "bg-emerald-500 hover:bg-emerald-600", text: "text-emerald-700", badge: "bg-emerald-100 text-emerald-700" },
  "99acres":   { bg: "bg-red-50",     border: "border-red-200",     btn: "bg-red-500 hover:bg-red-600",         text: "text-red-700",     badge: "bg-red-100 text-red-700" },
  housing:     { bg: "bg-blue-50",    border: "border-blue-200",    btn: "bg-blue-500 hover:bg-blue-600",       text: "text-blue-700",    badge: "bg-blue-100 text-blue-700" },
  magicbricks: { bg: "bg-orange-50",  border: "border-orange-200",  btn: "bg-orange-500 hover:bg-orange-600",   text: "text-orange-700",  badge: "bg-orange-100 text-orange-700" },
};

function Chip({ selected, onClick, icon: Icon, label, small }) {
  return (
    <button type="button" onClick={onClick}
      className={`flex items-center gap-1.5 rounded-xl border font-medium transition-all ${
        small ? "text-[11px] px-2.5 py-1" : "text-xs px-3 py-1.5"
      } ${selected ? "bg-amber-400 text-white border-amber-400 shadow-sm" : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-amber-200 dark:hover:border-amber-500"}`}>
      {Icon && <Icon size={small ? 11 : 13} weight={selected ? "fill" : "regular"} />}
      {label}
    </button>
  );
}

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); toast.success("Copied!"); setTimeout(() => setCopied(false), 2000); }}
      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 transition-colors">
      {copied ? <Check size={13} weight="bold" className="text-green-500" /> : <Copy size={13} />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

const PORTAL_BTN = {
  NoBroker:    "bg-emerald-500 hover:bg-emerald-600",
  "99acres":   "bg-red-500 hover:bg-red-600",
  "Housing.com":"bg-blue-500 hover:bg-blue-600",
  MagicBricks: "bg-orange-500 hover:bg-orange-600",
};
const PORTAL_BADGE = {
  NoBroker:    "bg-emerald-50 text-emerald-700 border-emerald-200",
  "99acres":   "bg-red-50 text-red-700 border-red-200",
  "Housing.com":"bg-blue-50 text-blue-700 border-blue-200",
  MagicBricks: "bg-orange-50 text-orange-700 border-orange-200",
};

function ListingCard({ listing }) {
  const btnCls  = PORTAL_BTN[listing.portal]  || "bg-slate-500 hover:bg-slate-600";
  const badgeCls = PORTAL_BADGE[listing.portal] || "bg-slate-50 text-slate-700 border-slate-200";
  const score = listing.rank_score || 0;
  const scoreCls = score >= 7 ? "text-emerald-600 bg-emerald-50 border-emerald-200"
    : score >= 5 ? "text-amber-600 bg-amber-50 border-amber-200"
    : "text-red-600 bg-red-50 border-red-200";

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col gap-3 h-full">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${badgeCls}`}>{listing.portal}</span>
            <span className={`flex items-center gap-0.5 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${scoreCls}`}>
              <Star size={9} weight="fill" /> {score}/10
            </span>
          </div>
          <p className="font-semibold text-slate-800 dark:text-white text-sm leading-snug line-clamp-2">{listing.title}</p>
          <p className="text-amber-500 font-mono font-bold text-sm mt-1">{listing.price}</p>
          <p className="text-slate-400 text-[11px] mt-0.5 flex items-center gap-1">
            <MapPin size={10} />{listing.location}
          </p>
        </div>
      </div>

      {listing.features?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {listing.features.map((f, i) => (
            <span key={i} className="text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-1.5 py-0.5 rounded-lg">{f}</span>
          ))}
        </div>
      )}

      {listing.airbnb_note && (
        <p className="text-[11px] text-slate-500 dark:text-slate-400 italic border-l-2 border-amber-300 pl-2 leading-snug flex-1">{listing.airbnb_note}</p>
      )}

      {listing.url ? (
        <a href={listing.url} target="_blank" rel="noopener noreferrer"
          className={`mt-auto flex items-center justify-center gap-1.5 text-xs font-semibold text-white rounded-xl py-2 transition-colors ${btnCls}`}>
          View on {listing.portal} <ArrowSquareOut size={11} />
        </a>
      ) : (
        <div className="mt-auto text-[10px] text-slate-400 text-center py-2">No direct link available</div>
      )}
    </div>
  );
}

function PortalCard({ id, portal }) {
  const s = PORTAL_STYLES[id] || PORTAL_STYLES["99acres"];
  return (
    <div className={`rounded-2xl border-2 ${s.border} ${s.bg} p-4 flex flex-col gap-3`}>
      <div className="flex items-start justify-between">
        <div>
          <p className={`font-bold text-base ${s.text}`}>{portal.name}</p>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${s.badge}`}>{portal.badge}</span>
        </div>
        <ArrowSquareOut size={16} className={s.text} />
      </div>
      <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
        Google-indexed listings from {portal.name} filtered to your city &amp; BHK size.
      </p>
      <a href={portal.url} target="_blank" rel="noopener noreferrer"
        className={`flex items-center justify-center gap-2 text-sm font-semibold text-white rounded-xl py-2.5 transition-colors ${s.btn}`}>
        Search on {portal.name}
        <ArrowSquareOut size={14} />
      </a>
    </div>
  );
}

export default function PropertyFinder() {
  const [acq, setAcq] = useState("rent");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [locality, setLocality] = useState("");
  const [bedrooms, setBedrooms] = useState("2BHK");
  const [budget, setBudget] = useState("");
  const [furnishing, setFurnishing] = useState("Any");
  const [amenities, setAmenities] = useState([]);
  const [near, setNear] = useState([]);
  const [extra, setExtra] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const toggle = (list, setList, id) => setList(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSearch = async () => {
    if (!city) { toast.error("Please select a city"); return; }
    if (!budget.trim()) { toast.error("Please enter your budget"); return; }
    setLoading(true); setResult(null);
    try {
      const res = await fetch(`${API}/property-finder`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ budget, city, state, locality, bedrooms, acquisition_type: acq,
          furnishing: furnishing === "Any" ? "" : furnishing, amenities, near, extra_requirements: extra }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || "Search failed"); }
      setResult(await res.json());
    } catch (err) { toast.error(err.message || "Something went wrong"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <OnboardingBanner pageKey="finder" />
      <div>
        <div className="flex items-center gap-2.5">
          <h1 className="font-manrope text-2xl font-bold text-slate-900 dark:text-amber-400 tracking-tight">Property Finder</h1>
        </div>
        <p className="text-slate-500 dark:text-slate-200 text-sm mt-1">AI-powered market analysis + portal links to NoBroker, 99acres, Housing.com &amp; MagicBricks. Individual listing search is improving.</p>
      </div>

      {/* â"€â"€ Form â"€â"€ */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5">
        {/* Acquisition */}
        <div>
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">I want to</p>
          <div className="grid grid-cols-3 gap-3">
            {ACQUISITION_TYPES.map(({ id, label, icon: Icon, desc }) => (
              <button key={id} type="button" onClick={() => { setAcq(id); setBudget(""); }}
                className={`flex flex-col items-center gap-1.5 rounded-2xl border-2 p-4 transition-all ${acq === id ? "border-amber-400 bg-amber-50 shadow-sm" : "border-slate-100 hover:border-slate-200"}`}>
                <Icon size={22} weight={acq === id ? "duotone" : "regular"} className={acq === id ? "text-amber-500" : "text-slate-400"} />
                <span className={`text-sm font-semibold ${acq === id ? "text-amber-700" : "text-slate-600 dark:text-slate-300"}`}>{label}</span>
                <span className="text-[10px] text-slate-400 text-center">{desc}</span>
              </button>
            ))}
          </div>
        </div>

        {/* State + City + Locality */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 block">State *</label>
            <Select value={state} onValueChange={v => { setState(v); setCity(""); }}>
              <SelectTrigger className="w-full bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100 rounded-xl h-10 text-sm focus:border-amber-400 focus:ring-amber-400/20">
                <SelectValue placeholder="Select state…" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-slate-800 border-slate-200 max-h-72 overflow-y-auto">
                {Object.keys(INDIA_CITIES).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 block">City *</label>
            <Select value={city} onValueChange={setCity} disabled={!state}>
              <SelectTrigger className="w-full bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100 rounded-xl h-10 text-sm focus:border-amber-400 focus:ring-amber-400/20">
                <SelectValue placeholder={state ? "Select city…" : "Pick state first"} />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-slate-800 border-slate-200 max-h-72 overflow-y-auto">
                {(INDIA_CITIES[state] || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 block">Locality / Area <span className="text-slate-400 font-normal">(optional)</span></label>
            <div className="relative">
              <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="text" placeholder="e.g. Koregaon Park, Baner, Calangute…" value={locality} onChange={e => setLocality(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleSearch()}
                className="w-full pl-8 pr-3 py-2.5 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition" />
            </div>
          </div>
        </div>

        {/* Size + Budget */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 block">Size</label>
            <div className="flex flex-wrap gap-1.5">
              {BEDROOM_OPTIONS.map(o => <Chip key={o} selected={bedrooms === o} onClick={() => setBedrooms(o)} label={o} small />)}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 block">Budget
              <span className="text-slate-400 font-normal ml-1">({acq === "purchase" ? "total" : acq === "rent" ? "per month" : "lump sum"})</span>
            </label>
            <div className="relative mb-1.5">
              <Money size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input type="text" placeholder={acq === "purchase" ? "e.g. 75 Lakhs" : acq === "rent" ? "e.g. 35,000" : "e.g. 20 Lakhs"}
                value={budget} onChange={e => setBudget(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSearch()}
                className="w-full pl-8 pr-3 py-2.5 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition" />
            </div>
            <div className="flex flex-wrap gap-1">
              {BUDGET_PRESETS[acq].map(p => (
                <button key={p} type="button" onClick={() => setBudget(p)}
                  className={`text-[10px] px-2 py-0.5 rounded-lg border transition-colors ${budget === p ? "bg-amber-400 text-white border-amber-400" : "bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-amber-200 dark:hover:border-amber-500"}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Furnishing */}
        <div>
          <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2 block">Furnishing</label>
          <div className="flex flex-wrap gap-2">
            {FURNISHING_OPTIONS.map(o => <Chip key={o} selected={furnishing === o} onClick={() => setFurnishing(o)} icon={o !== "Any" ? Couch : null} label={o} />)}
          </div>
        </div>

        {/* Amenities */}
        <div>
          <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2 block">Amenities Needed</label>
          <div className="flex flex-wrap gap-2">
            {AMENITIES.map(({ id, icon }) => <Chip key={id} selected={amenities.includes(id)} onClick={() => toggle(amenities, setAmenities, id)} icon={icon} label={id} />)}
          </div>
        </div>

        {/* Near */}
        <div>
          <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-2 block">Near</label>
          <div className="flex flex-wrap gap-2">
            {NEAR_OPTIONS.map(({ id, icon, label }) => <Chip key={id} selected={near.includes(id)} onClick={() => toggle(near, setNear, id)} icon={icon} label={label} />)}
          </div>
        </div>

        {/* Extra */}
        <div>
          <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 block">
            Additional Requirements <span className="text-slate-400 font-normal">(optional)</span>
          </label>
          <textarea placeholder="e.g. Ground floor preferred, pet-friendly, balcony, no society restrictions on Airbnb..."
            value={extra} onChange={e => setExtra(e.target.value)} rows={2}
            className="w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition resize-none" />
        </div>

        <button onClick={handleSearch} disabled={loading} type="button"
          className="w-full bg-amber-400 hover:bg-amber-500 disabled:opacity-50 text-white font-semibold rounded-xl py-3 text-sm transition-colors flex items-center justify-center gap-2 shadow-sm">
          {loading
            ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Analysing market…</>
            : <><MagnifyingGlass size={16} weight="bold" />Find Properties</>}
        </button>
      </div>

      {/* â"€â"€ Loading â"€â"€ */}
      {loading && (
        <div className="bg-white dark:bg-slate-800 border border-slate-100 rounded-2xl p-6 text-center space-y-2">
          <div className="w-6 h-6 border-2 border-amber-400/40 border-t-amber-400 rounded-full animate-spin mx-auto" />
          <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Searching listings &amp; analysing market…</p>
          <p className="text-xs text-slate-400">Scanning NoBroker · 99acres · Housing.com · MagicBricks</p>
        </div>
      )}

      {/* â"€â"€ Results â"€â"€ */}
      {result && !loading && (
        <div className="space-y-5">
          {/* Search summary pill */}
          {result.search_params && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-slate-500 dark:text-slate-400">Showing results for</span>
              {[result.search_params.bedrooms, result.search_params.acquisition_type, result.search_params.locality || result.search_params.city, result.search_params.budget]
                .filter(Boolean).map((tag, i) => (
                  <span key={i} className="bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-0.5 rounded-full font-medium capitalize">{tag}</span>
                ))}
            </div>
          )}

          {/* ── Ranked listings ── */}
          {result.listings?.length > 0 ? (
            <div>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Star size={16} className="text-amber-400" weight="fill" />
                <h2 className="font-semibold text-slate-800 dark:text-white">Ranked Listings</h2>
                <span className="text-xs text-slate-400">— sourced from Google index · ranked by Airbnb potential</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {result.listings.map((l, i) => <ListingCard key={i} listing={l} />)}
              </div>
              <p className="text-[10px] text-slate-400 mt-2 text-center">
                Results are from Google Search index — some listings may have been taken down since indexing.
              </p>
            </div>
          ) : result.portal_urls ? (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <ArrowSquareOut size={16} className="text-slate-300" />
                <h2 className="font-semibold text-slate-800 dark:text-white">Browse Live Listings</h2>
                <span className="text-xs text-slate-400">— pre-filtered to your search</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(result.portal_urls).map(([id, portal]) => (
                  <PortalCard key={id} id={id} portal={portal} />
                ))}
              </div>
            </div>
          ) : null}

          {/* Browse all fallback — always shown below listings */}
          {result.listings?.length > 0 && result.portal_urls && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-400 font-medium">Browse all on:</span>
              {Object.entries(result.portal_urls).map(([id, portal]) => (
                <a key={id} href={portal.url} target="_blank" rel="noopener noreferrer"
                  className="text-xs px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300 font-medium flex items-center gap-1.5 transition-colors">
                  {portal.name} <ArrowSquareOut size={10} />
                </a>
              ))}
            </div>
          )}

          {/* â"€â"€ Market Insight â"€â"€ */}
          {result.market_insight && (
            <div className="bg-gradient-to-r from-violet-50 to-blue-50 rounded-2xl border border-violet-100 p-5 flex gap-3">
              <Sparkle size={20} className="text-violet-500 shrink-0 mt-0.5" weight="fill" />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <p className="text-sm font-semibold text-violet-700">Market Insight — {result.search_params?.city || city}</p>
                  {result.live_data && (
                    <span className="flex items-center gap-1 text-[10px] text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-0.5 font-medium">
                      <Globe size={10} /> Live market data
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">{result.market_insight}</p>
              </div>
            </div>
          )}

          {/* â"€â"€ Airbnb ROI estimate â"€â"€ */}
          {result.airbnb_roi_estimate && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 shadow-sm p-5">
              <div className="flex items-center gap-2 mb-3">
                <TrendUp size={16} className="text-green-500" weight="bold" />
                <h2 className="font-semibold text-slate-800 dark:text-white text-sm">Airbnb Earnings Estimate</h2>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Monthly Revenue", val: result.airbnb_roi_estimate.monthly_revenue },
                  { label: "Occupancy", val: result.airbnb_roi_estimate.occupancy },
                  { label: "ROI Note", val: result.airbnb_roi_estimate.roi_note },
                ].map(({ label, val }) => (
                  <div key={label} className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3 border border-slate-100 dark:border-slate-600">
                    <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wide mb-1">{label}</p>
                    <p className="text-xs text-slate-700 dark:text-slate-200 font-medium leading-snug">{val}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* â"€â"€ What to expect + Red flags â"€â"€ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {result.what_to_expect?.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle size={15} className="text-green-500" weight="fill" />
                  <h2 className="font-semibold text-slate-800 dark:text-white text-sm">What to Expect</h2>
                </div>
                <ul className="space-y-2">
                  {result.what_to_expect.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-700 dark:text-slate-200">
                      <span className="text-green-400 mt-0.5 shrink-0">•</span>{item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.red_flags?.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                  <SealWarning size={15} className="text-red-500" weight="fill" />
                  <h2 className="font-semibold text-slate-800 dark:text-white text-sm">Red Flags to Watch</h2>
                </div>
                <ul className="space-y-2">
                  {result.red_flags.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-700 dark:text-slate-200">
                      <span className="text-red-400 mt-0.5 shrink-0">âš </span>{item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* â"€â"€ Negotiation tip + Site visit checklist â"€â"€ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {result.negotiation_tips && (
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Handshake size={15} className="text-amber-500" weight="fill" />
                  <h2 className="font-semibold text-slate-800 dark:text-white text-sm">Negotiation Tips</h2>
                </div>
                <p className="text-xs text-slate-700 dark:text-slate-200 leading-relaxed">{result.negotiation_tips}</p>
              </div>
            )}
            {result.checklist?.length > 0 && (
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 shadow-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                  <ClipboardText size={15} className="text-blue-500" weight="fill" />
                  <h2 className="font-semibold text-slate-800 dark:text-white text-sm">Site Visit Checklist</h2>
                </div>
                <ul className="space-y-1.5">
                  {result.checklist.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-slate-700 dark:text-slate-200">
                      <span className="text-blue-400 shrink-0 mt-0.5">â˜</span>{item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* â"€â"€ Visit message â"€â"€ */}
          {result.visit_message && (
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-semibold text-slate-800 dark:text-white">Visit Request Message</h2>
                  <p className="text-xs text-slate-400 mt-0.5">Copy and send via WhatsApp or email</p>
                </div>
                <CopyBtn text={result.visit_message} />
              </div>
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl border border-slate-100 dark:border-slate-600 p-4">
                <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-wrap">{result.visit_message}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
